import React, { useId, useEffect, useRef } from 'react';
import type { PendingActionPreview } from '../../types';

interface PreviewCardProps {
  preview: PendingActionPreview;
  state: 'pending' | 'creating' | 'created' | 'declined' | 'superseded';
  onConfirm: () => void;
  onAdjust: () => void;
  onDecline: () => void;
}

/**
 * PreviewCard (AC-1646, epic AC-1639). Shows the plan the assistant intends to
 * execute and gates creation behind an explicit Confirm. The card is an
 * accessible role="group" region whose label ("Review before creating") and
 * description (a resource summary) are wired via aria-labelledby /
 * aria-describedby. It lists the indexed steps, the agents/queues to create, and
 * any structural repairs as text (never color-only).
 *
 * Three real buttons sit in DOM/tab order Confirm & create -> Adjust -> Decline.
 * State drives affordances:
 *   - 'creating'   : all buttons disabled + aria-busy on the container.
 *   - 'created'    : a created indicator replaces the actions.
 *   - 'declined'   : a dimmed "Declined - not created" note + a "Re-create this
 *                    plan" button (re-uses onConfirm).
 *   - 'superseded' : Confirm is disabled (the plan changed under the user).
 *
 * Laid out for a ~320px assistant panel: vertical stacking, a full-width primary
 * Confirm, >=44px tap targets, and long resource names truncated with a title.
 *
 * AC-1784 (epic AC-1778): a RUN variant keyed on
 * action_type === 'preview_run_orchestration'. It proposes EXECUTING the
 * in-context workflow rather than creating/updating one — heading "Run this
 * workflow?", body shows workflow_name + step_count + run_caution (and the
 * optional input_message echo), primary button "Confirm & run". A run preview
 * has no "Adjust" affordance (confirm-or-decline only) and a run decline reads
 * a neutral "Declined" with no re-create button.
 */
// AC-1777: human-readable label for a previewed step so the user can tell WHAT
// each step does (its TYPE + action), not just read a bare description. An
// auto_prompt initiator reads as `Auto-prompt -- runs "Summarize today's
// emails"`; a run_agent reads as `Agent (create intent) -- <task>`. The
// description is preserved verbatim inside the label.
const STEP_TYPE_LABELS: Record<string, string> = {
  auto_prompt: 'Auto-prompt',
  user_prompt: 'User prompt',
  run_agent: 'Agent',
  conditional: 'Conditional',
  filter: 'Filter',
  queue_read: 'Queue read',
  queue_write: 'Queue write',
  rdap_lookup: 'RDAP lookup',
};

function describeStep(step: {
  step_type: string;
  intent?: string;
  description?: string;
}): string {
  const label = STEP_TYPE_LABELS[step.step_type] ?? step.step_type;
  const desc = step.description?.trim();
  switch (step.step_type) {
    case 'auto_prompt':
      return desc ? `${label} — runs "${desc}"` : label;
    case 'user_prompt':
      return desc ? `${label} — asks "${desc}"` : label;
    case 'run_agent': {
      const intent = step.intent ? ` (${step.intent} intent)` : '';
      return desc ? `${label}${intent} — ${desc}` : `${label}${intent}`;
    }
    default:
      return desc ? `${label} — ${desc}` : label;
  }
}

const PreviewCard: React.FC<PreviewCardProps> = ({
  preview,
  state,
  onConfirm,
  onAdjust,
  onDecline,
}) => {
  const headingId = useId();
  const summaryId = useId();

  // AC-1646 a11y: move focus to the card when it first appears so keyboard /
  // screen-reader users land on the "Review before creating" region (which
  // announces its label + resource summary) rather than being stranded at the
  // input. Fires once on mount; only steals focus for an actionable preview.
  const regionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state === 'pending') {
      regionRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCreating = state === 'creating';
  const isDeclined = state === 'declined';
  const isCreated = state === 'created';
  const isSuperseded = state === 'superseded';

  // AC-1784 (epic AC-1778): a run preview proposes to EXECUTE the in-context
  // workflow rather than create or update resources. Detect it strictly from
  // the action_type the backend sends. A run preview is the dominant branch —
  // it short-circuits the create/update layout below and renders the run
  // variant (workflow name + step count + execute caution).
  const isRun = preview.action_type === 'preview_run_orchestration';

  // AC-1659: an update preview proposes edits to an existing orchestration
  // rather than creating new resources. Detect it from the action_type the
  // backend (AC-1657) sends, or defensively from the presence of the
  // summary/changes fields. A run preview never counts as an update even if
  // some legacy field is incidentally present.
  const changes = preview.changes ?? [];
  const isUpdate =
    !isRun &&
    (preview.action_type === 'preview_update_orchestration' ||
      !!preview.summary ||
      changes.length > 0);

  // Human-readable summary of what the plan will produce. For a create, names
  // the step count and the resources; for an update, uses the backend-provided
  // summary line so the aria-describedby announcement is meaningful.
  const summaryParts: string[] = [
    `${preview.step_count} step${preview.step_count === 1 ? '' : 's'}`,
  ];
  if (preview.agents_to_create.length > 0) {
    summaryParts.push(`agents ${preview.agents_to_create.join(', ')}`);
  }
  if (preview.queues_to_create.length > 0) {
    summaryParts.push(`queue${preview.queues_to_create.length === 1 ? '' : 's'} ${preview.queues_to_create.join(', ')}`);
  }
  if (preview.models_to_create.length > 0) {
    summaryParts.push(`model${preview.models_to_create.length === 1 ? '' : 's'} ${preview.models_to_create.join(', ')}`);
  }
  // AC-1784: run summary names the workflow and step count so the
  // aria-describedby announcement tells the user exactly what will execute.
  const runStepText = `${preview.step_count} step${preview.step_count === 1 ? '' : 's'}`;
  const runSummaryText = preview.workflow_name
    ? `Will run "${preview.workflow_name}" (${runStepText}).`
    : `Will run this workflow (${runStepText}).`;

  const summaryText = isRun
    ? runSummaryText
    : isUpdate
    ? preview.summary || 'Review the proposed changes before updating.'
    : `Will create ${summaryParts.join('; ')}.`;

  const headingText = isRun
    ? 'Run this workflow?'
    : isUpdate
    ? 'Review before updating'
    : 'Review before creating';
  const confirmLabel = isRun
    ? 'Confirm & run'
    : isUpdate
    ? 'Confirm & update'
    : 'Confirm & create';

  return (
    <div
      ref={regionRef}
      role="group"
      tabIndex={-1}
      aria-labelledby={headingId}
      aria-describedby={summaryId}
      aria-live="polite"
      aria-busy={isCreating ? 'true' : undefined}
      className={`mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 focus:outline-none ${
        isDeclined ? 'opacity-50' : ''
      }`}
    >
      <div>
        <h4
          id={headingId}
          className="text-sm font-semibold text-gray-900 dark:text-warm-white"
        >
          {headingText}
        </h4>
        <p id={summaryId} className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
          {summaryText}
        </p>
      </div>

      {/* RUN preview (AC-1784): name the workflow + step count, show the
          always-present execute caution as text (never color-only), and echo
          the optional input override so the user confirms the exact input the
          run will receive. Replaces the create/update resource blocks for a
          run preview. */}
      {isRun ? (
        <>
          <dl className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
            {preview.workflow_name && (
              <div className="flex gap-1">
                <dt className="font-medium">Workflow:</dt>
                <dd className="break-words" title={preview.workflow_name}>
                  {preview.workflow_name}
                </dd>
              </div>
            )}
            <div className="flex gap-1">
              <dt className="font-medium">Steps:</dt>
              <dd>{preview.step_count}</dd>
            </div>
            {preview.input_message && (
              <div className="flex gap-1">
                <dt className="font-medium">Input:</dt>
                <dd className="break-words" title={preview.input_message}>
                  "{preview.input_message}"
                </dd>
              </div>
            )}
          </dl>

          {preview.run_caution && (
            <p className="text-xs text-amber-700 dark:text-amber-400 break-words">
              {preview.run_caution}
            </p>
          )}
        </>
      ) : isUpdate ? (
        <>
          <ul className="space-y-2">
          {changes.map((change, index) => {
            const field = change.field ?? change.name ?? `Change ${index + 1}`;
            const before = change.before;
            const after = change.after;
            const target = change.step || change.agent;
            return (
              <li
                key={index}
                className="text-xs text-gray-700 dark:text-gray-300"
              >
                <div className="font-medium text-gray-900 dark:text-warm-white">
                  {String(field)}
                  {target && (
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                      {' '}
                      ({String(target)})
                    </span>
                  )}
                </div>
                {(before !== undefined || after !== undefined) && (
                  <div className="mt-0.5 flex flex-col gap-0.5">
                    {/* AC-1669: render the struck "before" only when there is a
                        prior value (absent for an ADD tool change) and the
                        "-> after" only when there is a new value (absent for a
                        REMOVE), so add/remove changes don't show a stray empty
                        strikethrough or arrow line. */}
                    {before !== undefined && before !== null && String(before) !== '' && (
                      <span className="text-red-600 dark:text-red-400 line-through break-words">
                        {String(before)}
                      </span>
                    )}
                    {after !== undefined && after !== null && String(after) !== '' && (
                      <span className="text-green-700 dark:text-green-400 break-words">
                        &rarr; {String(after)}
                      </span>
                    )}
                  </div>
                )}
                {Array.isArray(change.affected_agents) && change.affected_agents.length > 0 && (
                  <div className="mt-0.5 text-amber-700 dark:text-amber-400 break-words">
                    Also affects: {change.affected_agents.join(', ')}
                  </div>
                )}
              </li>
            );
          })}
          </ul>
          {/* AC-1692: schemas this update will create (e.g. via set_output_schema
              or nested children) - mirror the create-branch New schemas block so
              the user sees what's added when editing/wiring a schema. */}
          {preview.models_to_create.length > 0 && (
            <div className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">New schemas:</span>
              <ul className="mt-0.5 space-y-0.5">
                {preview.models_to_create.map((name) => (
                  <li key={name} className="break-words" title={name}>
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Steps, indexed in order */}
          <ol className="space-y-1">
            {preview.steps.map((step) => (
              <li
                key={step.order}
                className="text-xs text-gray-700 dark:text-gray-300"
              >
                <span className="font-medium">{step.order}.</span>{' '}
                {describeStep(step)}
                {step.agent && (
                  <span
                    className="text-blue-600 dark:text-blue-400 truncate"
                    title={step.agent}
                  >
                    {' '}
                    - {step.agent}
                  </span>
                )}
              </li>
            ))}
          </ol>

          {/* Agents to create (AC-1677): render one per line so an enriched
              entry's template provenance and attached tools (e.g. "X (from
              template Web Research Agent; tools: web_search)") stays readable
              instead of being clipped/crammed inline. */}
          {preview.agents_to_create.length > 0 && (
            <div className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">New agents:</span>
              <ul className="mt-0.5 space-y-0.5">
                {preview.agents_to_create.map((name) => (
                  <li key={name} className="break-words" title={name}>
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Agents-as-tools wiring (AC-1721/AC-1722): when a run_agent step's
              orchestrator agent uses other agents as tools, name each edge
              "<specialist> as <tool_name>" so the user sees the delegation the
              orchestrator will perform before confirming. */}
          {(preview.agent_tools_to_attach?.length ?? 0) > 0 && (
            <div className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">Agent tools:</span>
              <ul className="mt-0.5 space-y-0.5">
                {preview.agent_tools_to_attach!.map((edge, index) => {
                  const label = edge.orchestrator
                    ? `${edge.child_agent} as ${edge.tool_name} (on ${edge.orchestrator})`
                    : `${edge.child_agent} as ${edge.tool_name}`;
                  return (
                    <li key={index} className="break-words" title={label}>
                      {edge.child_agent} as{' '}
                      <span className="font-mono">{edge.tool_name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Queues to create */}
          {preview.queues_to_create.length > 0 && (
            <div className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">New queues:</span>{' '}
              {preview.queues_to_create.map((name) => (
                <span key={name} className="truncate" title={name}>
                  {name}{' '}
                </span>
              ))}
            </div>
          )}

          {/* Schemas (structured-output models) to create (AC-1686): one per
              line, mirroring the New agents block, so a created schema is
              visible in the preview. */}
          {preview.models_to_create.length > 0 && (
            <div className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">New schemas:</span>
              <ul className="mt-0.5 space-y-0.5">
                {preview.models_to_create.map((name) => (
                  <li key={name} className="break-words" title={name}>
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Repairs, as text (not color-only) */}
      {preview.repairs.length > 0 && (
        <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
          {preview.repairs.map((repair, index) => (
            <li key={index}>Fix: {repair}</li>
          ))}
        </ul>
      )}

      {/* Actions / status */}
      {isCreated ? (
        // AC-1784: a confirmed RUN reads "Running" (the CompactRunProgressCard
        // in the follow-up turn carries the live status); create/update read
        // "Created".
        <div className="text-xs font-medium text-green-700 dark:text-green-400">
          {isRun ? 'Running' : 'Created'}
        </div>
      ) : isDeclined ? (
        <div className="flex flex-col gap-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {/* AC-1784: a run decline reads "Declined" (nothing was created,
                and re-running is a fresh ask, not a re-create of this plan). */}
            {isRun ? 'Declined' : 'Declined — not created'}
          </div>
          {!isRun && (
            <button
              type="button"
              onClick={onConfirm}
              className="w-full min-h-[44px] text-sm font-medium bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
            >
              Re-create this plan
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isCreating || isSuperseded}
            className="w-full min-h-[44px] text-sm font-medium bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {confirmLabel}
          </button>
          <div className="flex gap-2">
            {/* AC-1784: a run preview has no "Adjust" affordance — running the
                in-context workflow is confirm-or-decline only. Create/update
                keep Adjust so the user can drive a re-plan loop. */}
            {!isRun && (
              <button
                type="button"
                onClick={onAdjust}
                disabled={isCreating}
                className="flex-1 min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Adjust
              </button>
            )}
            <button
              type="button"
              onClick={onDecline}
              disabled={isCreating}
              className="flex-1 min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewCard;
